import { authStore } from '../stores/authStore';
const API_BASE = '/api/v1';
async function fetchApi(endpoint, options) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    if (result.status !== 'OK') {
        throw new Error(result.status);
    }
    return result.data;
}
// Fetch API with Authorization header for protected endpoints
async function fetchApiWithAuth(endpoint, options) {
    const authHeader = await authStore.getAuthHeader();
    const headers = {
        'Content-Type': 'application/json',
        ...options?.headers,
    };
    if (authHeader) {
        headers['Authorization'] = authHeader;
    }
    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
    });
    if (response.status === 401) {
        throw new Error('Unauthorized: Please log in to perform this action');
    }
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    if (result.status !== 'OK') {
        throw new Error(result.status);
    }
    return result.data;
}
// Test runs list
export async function getTestRuns(testId) {
    const params = testId ? `?test_id=${encodeURIComponent(testId)}` : '';
    return fetchApi(`/test_runs${params}`);
}
// Tests (registry) list
export async function getTests() {
    return fetchApi('/tests');
}
// Test details (uses auth to get vars with global variables merged in)
export async function getTestDetails(testId) {
    return fetchApiWithAuth(`/test/${encodeURIComponent(testId)}`);
}
// Test YAML source (for loading existing tests in builder)
export async function getTestYaml(testId) {
    return fetchApiWithAuth(`/test/${encodeURIComponent(testId)}/yaml`);
}
// Clients list
export async function getClients() {
    return fetchApi('/clients');
}
// Test run details
export async function getTestRunDetails(runId) {
    return fetchApiWithAuth(`/test_run/${runId}/details`);
}
// Latest run-level result markdown for a test (envelope form). Walks
// the newest runs server-side and returns the first one that produced
// a $ASSERTOOR_TEST_RESULT blob. `run_id === 0` and empty `markdown`
// indicate "no result available across recent runs" (the dashboard
// tile renders an empty state in that case).
export async function getLatestTestResult(testId) {
    return fetchApi(`/test/${encodeURIComponent(testId)}/latest_result?meta=1`).then((data) => {
        // Server returns `data: null` on 200 when no result exists yet —
        // normalise to an empty envelope so callers don't have to.
        return (data ?? {
            run_id: 0,
            status: '',
            start_time: 0,
            stop_time: 0,
            markdown: '',
        });
    });
}
// Run-level Result markdown. Returns null when the run has not produced
// a $ASSERTOOR_TEST_RESULT blob (server replies 204 No Content).
export async function getTestRunResult(runId) {
    const response = await fetch(`${API_BASE}/test_run/${runId}/result`);
    if (response.status === 204)
        return null;
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.text();
}
// Task details
export async function getTaskDetails(runId, taskIndex) {
    return fetchApiWithAuth(`/test_run/${runId}/task/${taskIndex}/details`);
}
// Task descriptors
export async function getTaskDescriptors() {
    return fetchApi('/task_descriptors');
}
export async function getTaskDescriptor(name) {
    return fetchApi(`/task_descriptor/${encodeURIComponent(name)}`);
}
// Global variable names (auth required)
export async function getGlobalVariables() {
    return fetchApiWithAuth('/global_variables');
}
// Build version info
export async function getVersion() {
    return fetchApi('/version');
}
// Test schedule (cron + startup) — read open, write auth-gated.
export async function updateTestSchedule(testId, schedule) {
    await fetchApiWithAuth(`/test/${encodeURIComponent(testId)}/schedule`, {
        method: 'PUT',
        body: JSON.stringify({ schedule }),
    });
}
export async function getTestNextRun(testId) {
    return fetchApi(`/test/${encodeURIComponent(testId)}/next_run`);
}
// Live test queue (running + pending) used by the StartTestModal's
// QueuePicker.
export async function getTestQueue() {
    return fetchApi('/test_queue');
}
// Admin operations (require authentication)
export async function scheduleTestRun(request) {
    return fetchApiWithAuth('/test_runs/schedule', {
        method: 'POST',
        body: JSON.stringify(request),
    });
}
export async function cancelTestRun(runId) {
    await fetchApiWithAuth(`/test_run/${runId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({}),
    });
}
export async function deleteTestRuns(runIds) {
    await fetchApiWithAuth('/test_runs/delete', {
        method: 'POST',
        body: JSON.stringify({ test_runs: runIds }),
    });
}
export async function deleteTest(testId) {
    await fetchApiWithAuth('/tests/delete', {
        method: 'POST',
        body: JSON.stringify({ tests: [testId] }),
    });
}
export async function registerTest(yaml) {
    // Send raw YAML with application/yaml content type
    // The backend expects either YAML body or JSON with test fields directly
    const authHeader = await authStore.getAuthHeader();
    const headers = {
        'Content-Type': 'application/yaml',
    };
    if (authHeader) {
        headers['Authorization'] = authHeader;
    }
    const response = await fetch(`${API_BASE}/tests/register`, {
        method: 'POST',
        headers,
        body: yaml,
    });
    if (response.status === 401) {
        throw new Error('Unauthorized: Please log in to perform this action');
    }
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    if (result.status !== 'OK') {
        throw new Error(result.status);
    }
}
export async function registerExternalTest(url, options) {
    return fetchApiWithAuth('/tests/register_external', {
        method: 'POST',
        body: JSON.stringify({ file: url, name: options?.name }),
    });
}
// Dashboard config
//
// The server stores an opaque JSON blob (the schema is owned by the
// client). GET is open; PUT requires authentication. A 204 from GET
// means "no config yet" and the UI falls back to its built-in
// default dashboard.
export async function getDashboardConfig() {
    const response = await fetch(`${API_BASE}/dashboard_config`);
    if (response.status === 204)
        return null;
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function putDashboardConfig(cfg) {
    const authHeader = await authStore.getAuthHeader();
    const headers = {
        'Content-Type': 'application/json',
    };
    if (authHeader)
        headers['Authorization'] = authHeader;
    const response = await fetch(`${API_BASE}/dashboard_config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(cfg),
    });
    if (response.status === 401) {
        throw new Error('Unauthorized: log in to save dashboard changes');
    }
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
}
export async function getNetworkStatus() {
    return fetchApi('/network_status');
}
// Playbook library
export async function getPlaybookLibrary() {
    return fetchApi('/playbook_library');
}
export async function checkPlaybookLibrary(file) {
    return fetchApiWithAuth(`/playbook_library/check?file=${encodeURIComponent(file)}`);
}
